import React, { useState, useEffect } from 'react';
import { Card, Typography, Button, Table, Space, Tag, Modal, Form, Input, InputNumber, Switch, Popconfirm, message } from 'antd';
import { ToolOutlined, PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { useAuth } from '../../../aws/AuthContext';
import axios from 'axios';

const { Title, Text } = Typography;
const API_BASE_URL = process.env.REACT_APP_API_URL;

const SeccionServicios = () => {
    const { getJwtToken } = useAuth();
    const [servicios, setServicios] = useState([]);
    const [loading, setLoading] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [servicioSeleccionado, setServicioSeleccionado] = useState(null);
    const [form] = Form.useForm();

    const fetchServicios = async () => {
        setLoading(true);
        try {
            const token = await getJwtToken();
            const res = await axios.get(`${API_BASE_URL}/v1-tipos-servicio`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setServicios(res.data);
        } catch (error) {
            message.error("Error al cargar los servicios");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchServicios();
    }, []);

    const handleOpenModal = (record = null) => {
        setIsEditing(!!record);
        setServicioSeleccionado(record);
        if (record) {
            form.setFieldsValue({
                nombre: record.nombre,
                precio_base: record.precio_base ? Number(record.precio_base) : null,
                estado: record.estado
            });
        } else {
            form.resetFields();
            form.setFieldsValue({ estado: true }); // Activo por defecto
        }
        setIsModalOpen(true);
    };

    const handleSubmit = async (values) => {
        setLoading(true);
        try {
            const token = await getJwtToken();
            const config = { headers: { Authorization: `Bearer ${token}` } };

            if (isEditing) {
                await axios.put(`${API_BASE_URL}/v1-tipos-servicio/${servicioSeleccionado.id}`, values, config);
                message.success("Servicio actualizado correctamente");
            } else {
                await axios.post(`${API_BASE_URL}/v1-tipos-servicio`, values, config);
                message.success("Servicio creado exitosamente");
            }
            setIsModalOpen(false);
            fetchServicios();
        } catch (error) {
            message.error("Error al guardar el servicio");
        } finally {
            setLoading(false);
        }
    };

    const handleToggleEstado = async (checked, record) => {
        try {
            const token = await getJwtToken();
            await axios.put(`${API_BASE_URL}/v1-tipos-servicio/${record.id}`, {
                estado: checked
            }, { headers: { Authorization: `Bearer ${token}` } });
            message.success(`Servicio ${checked ? 'activado' : 'desactivado'}`);
            fetchServicios();
        } catch (error) {
            message.error("Error al cambiar estado");
        }
    };

    const handleDelete = async (id) => {
        try {
            const token = await getJwtToken();
            await axios.delete(`${API_BASE_URL}/v1-tipos-servicio/${id}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            message.success("Servicio eliminado del catálogo");
            fetchServicios();
        } catch (error) {
            message.error("Error al eliminar el servicio");
        }
    };

    const columns = [
        {
            title: 'Nombre del Servicio',
            dataIndex: 'nombre',
            render: (text) => <Text strong>{text}</Text>
        },
        {
            title: 'Precio Base',
            dataIndex: 'precio_base',
            render: (val) => val ? <Text strong style={{ color: '#22BABB' }}>${Number(val).toLocaleString('es-CO')}</Text> : <Text type="secondary">Variable (Libre)</Text>
        },
        {
            title: 'Estado',
            dataIndex: 'estado',
            align: 'center',
            render: (estado, record) => (
                <Space>
                    <Switch
                        checked={estado}
                        onChange={(checked) => handleToggleEstado(checked, record)}
                        style={{ backgroundColor: estado ? '#22BABB' : '#cbd5e1' }}
                    />
                    <Tag color={estado ? 'green' : 'default'} style={{ margin: 0 }}>
                        {estado ? 'ACTIVO' : 'INACTIVO'}
                    </Tag>
                </Space>
            )
        },
        {
            title: 'Acciones',
            align: 'right',
            render: (_, record) => (
                <Space>
                    <Button
                        type="text"
                        icon={<EditOutlined />}
                        onClick={() => handleOpenModal(record)}
                        style={{ color: '#22BABB' }}
                    />
                    <Popconfirm
                        title="¿Eliminar servicio?"
                        description="El historial de facturas se mantendrá."
                        onConfirm={() => handleDelete(record.id)}
                        okText="Sí, eliminar"
                        cancelText="Cancelar"
                        okButtonProps={{ danger: true }}
                    >
                        <Button type="text" danger icon={<DeleteOutlined />} />
                    </Popconfirm>
                </Space>
            )
        }
    ];

    return (
        <div style={{ animation: 'fadeIn 0.5s' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
                <div>
                    <Title level={3} style={{ margin: 0 }}>Catálogo de Servicios</Title>
                    <Text type="secondary">Crea y administra los servicios de reparación, mantenimiento o mano de obra.</Text>
                </div>
                <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    size="large"
                    onClick={() => handleOpenModal()}
                    style={{ backgroundColor: '#22BABB', borderRadius: '8px', fontWeight: 'bold' }}
                >
                    Nuevo Servicio
                </Button>
            </div>

            <Card bordered={false} style={{ borderRadius: '16px', boxShadow: '0 2px 4px rgba(0,0,0,0.02)' }}>
                <Table
                    columns={columns}
                    dataSource={servicios}
                    rowKey="id"
                    loading={loading}
                    pagination={{ pageSize: 10 }}
                    locale={{ emptyText: 'No has creado ningún servicio aún.' }}
                />
            </Card>

            <Modal
                title={
                    <Space size="middle" align="center">
                        <div style={{ background: '#ccfbf1', padding: '10px', borderRadius: '12px', display: 'flex' }}>
                            <ToolOutlined style={{ color: '#0f766e', fontSize: '22px' }} />
                        </div>
                        <span style={{ fontSize: '20px', fontWeight: '900', color: '#0f172a' }}>
                            {isEditing ? 'Editar Servicio' : 'Nuevo Servicio'}
                        </span>
                    </Space>
                }
                open={isModalOpen}
                onCancel={() => setIsModalOpen(false)}
                footer={null}
                centered
                width={550}
                styles={{ body: { paddingTop: '20px' } }}
            >
                <Form form={form} layout="vertical" onFinish={handleSubmit}>
                    <Form.Item
                        name="nombre"
                        label={<Text strong style={{ fontSize: '14px', color: '#334155' }}>Nombre del Servicio</Text>}
                        rules={[{ required: true, message: 'El nombre es obligatorio' }]}
                    >
                        <Input
                            style={{ height: '50px', borderRadius: '12px', fontSize: '16px', fontWeight: '600' }}
                            placeholder="Ej: Cambio de pantalla, Limpieza profunda..."
                        />
                    </Form.Item>

                    <Form.Item
                        name="precio_base"
                        label={<Text strong style={{ fontSize: '14px', color: '#334155' }}>Precio Base <Text type="secondary" style={{ fontWeight: 'normal' }}>(Opcional)</Text></Text>}
                        extra={<span style={{ fontSize: '13px', color: '#64748b' }}>Déjalo vacío si el cajero debe ingresar el precio manualmente en cada venta.</span>}
                    >
                        <InputNumber
                            style={{ width: '100%', height: '50px', borderRadius: '12px', display: 'flex', alignItems: 'center', fontSize: '17px', fontWeight: '600' }}
                            prefix={<span style={{ color: '#94a3b8', marginRight: '8px', fontSize: '18px' }}>$</span>}
                            formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, '.')}
                            parser={value => value.replace(/\$\s?|(\.*)/g, '')}
                            placeholder="0"
                            controls={false}
                        />
                    </Form.Item>

                    {isEditing && (
                        <div style={{ background: '#f8fafc', padding: '16px 20px', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', border: '1px solid #e2e8f0', marginBottom: '24px' }}>
                            <Text strong style={{ fontSize: '15px', color: '#334155' }}>Estado del servicio</Text>
                            <Form.Item name="estado" valuePropName="checked" noStyle>
                                <Switch checkedChildren="Activo" unCheckedChildren="Inactivo" />
                            </Form.Item>
                        </div>
                    )}

                    <Button
                        type="primary"
                        htmlType="submit"
                        block
                        loading={loading}
                        style={{ backgroundColor: '#22BABB', height: '55px', borderRadius: '12px', fontSize: '16px', fontWeight: '800', marginTop: isEditing ? '0' : '10px', border: 'none', boxShadow: '0 4px 10px rgba(34, 186, 187, 0.3)' }}
                    >
                        {isEditing ? 'ACTUALIZAR SERVICIO' : 'GUARDAR SERVICIO'}
                    </Button>
                </Form>
            </Modal>
        </div>
    );
};

export default SeccionServicios;